import { useState, useCallback, useEffect, useRef } from 'react';
import { Plus } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { API_URL } from '../../config/constants';
import AddTransactionModal from './AddTransactionModal';
import { Z } from '../../constants/zIndex';
import Button from './Button';

/**
 * GlobalFAB — desktop-only floating action button for adding a manual transaction.
 * Mounts globally in App.js so it persists across all authenticated routes.
 */
export default function GlobalFAB() {
  const { isAuthenticated, onboardingComplete, household, authenticatedFetch } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isSuppressed, setIsSuppressed] = useState(() => (
    typeof document !== 'undefined' && document.body.classList.contains('hide-global-fab')
  ));
  const categoriesRef = useRef(null); // cache so we don't re-fetch if already loaded

  const open = useCallback(async () => {
    setIsOpen(true);
    // Fetch categories if not already cached
    if (categoriesRef.current) {
      setCategories(categoriesRef.current);
      return;
    }
    try {
      const res = await authenticatedFetch(`${API_URL}/categories/${household?.id}`);
      if (res.ok) {
        const data = await res.json();
        categoriesRef.current = data;
        setCategories(data);
      }
    } catch {
      // Non-fatal — user can still submit without a category
    }
  }, [authenticatedFetch, household?.id]);

  const close = useCallback(() => setIsOpen(false), []);

  // Clear category cache if household changes (e.g. after logout/login)
  useEffect(() => {
    categoriesRef.current = null;
    setCategories([]);
  }, [household?.id]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const syncSuppression = () => setIsSuppressed(document.body.classList.contains('hide-global-fab'));
    syncSuppression();

    const observer = new MutationObserver(syncSuppression);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  if (!isAuthenticated || !onboardingComplete || !household?.id || isSuppressed) return null;

  return (
    <>
      <Button
        type="button"
        variant="primary"
        size="none"
        onClick={open}
        aria-label="Add transaction"
        title="Add transaction"
        className="global-add-transaction-fab fixed bottom-5 right-5 h-12 w-12 flex items-center justify-center"
        style={{ zIndex: Z.ADD_TRANSACTION_FAB }}
      >
        <Plus className="h-5 w-5" strokeWidth={2.25} />
      </Button>

      <AddTransactionModal
        isOpen={isOpen}
        onClose={close}
        categories={categories}
      />
    </>
  );
}
